import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import {
  ChevronDown,
  ChevronRight,
  Phone,
  Mail,
  MapPin,
  Truck,
  Users,
  FileText,
  DollarSign,
  Package,
  ArrowDownToLine,
  ArrowUpFromLine,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Contact = {
  name: string;
  role: string;
  phone: string;
  email: string;
};

type Deal = {
  id: string;
  name: string;
  client_tag: string;
  direction: "inbound" | "outbound";
  material: string;
  address: string;
  pricing_notes: string;
  load_frequency: string;
  contacts: Contact[];
};

type TeamMember = {
  name: string;
  role: string;
  phone: string;
  email: string;
};

type Driver = {
  name: string;
  phone: string;
};

// ---------------------------------------------------------------------------
// Static data (reference info that rarely changes)
// ---------------------------------------------------------------------------

const DEALS: Deal[] = [
  {
    id: "vanguard",
    name: "Vanguard / Tyson",
    client_tag: "vanguard",
    direction: "inbound",
    material: "Corn dog waste processing - food manufacturing byproduct",
    address: "2100 N 24th St, Phoenix, AZ 85008",
    pricing_notes: "Per-load fee, net 30 terms. COD for special hauls.",
    load_frequency: "2-3 loads per week, M-F scheduling",
    contacts: [
      { name: "Casey Mitchell", role: "Account Manager", phone: "(602) 555-0101", email: "casey@vanguardrenewables.com" },
      { name: "Dan Pryor", role: "Operations Lead", phone: "(602) 555-0102", email: "dan@vanguardrenewables.com" },
      { name: "Mike Torres", role: "Plant Supervisor", phone: "(602) 555-0103", email: "mike.t@vanguardrenewables.com" },
      { name: "Sarah Lin", role: "Logistics Coordinator", phone: "(602) 555-0104", email: "sarah.lin@vanguardrenewables.com" },
      { name: "AP Department", role: "Accounts Payable", phone: "(857) 555-0100", email: "ServicesAP@vanguardrenewables.com" },
      { name: "James Whitfield", role: "Regional Director", phone: "(602) 555-0106", email: "james.w@vanguardrenewables.com" },
      { name: "Patricia Gomez", role: "Quality Control", phone: "(602) 555-0107", email: "patricia.g@vanguardrenewables.com" },
      { name: "Robert Chen", role: "Environmental Compliance", phone: "(602) 555-0108", email: "robert.c@vanguardrenewables.com" },
    ],
  },
  {
    id: "willcox",
    name: "Willcox Pistachio",
    client_tag: "willcox",
    direction: "outbound",
    material: "Pistachio orchard blend - custom compost mix for tree crops",
    address: "Willcox, AZ (Cochise County)",
    pricing_notes: "Per-yard pricing, delivery included for 20+ yd orders.",
    load_frequency: "Seasonal - heavy spring/fall, 1-2 loads/month off-season",
    contacts: [
      { name: "Shawn", role: "Ranch Owner", phone: "(520) 555-0201", email: "scdvm@yahoo.com" },
      { name: "Juan", role: "Farm Manager", phone: "(520) 555-0202", email: "jwproduce@live.com" },
    ],
  },
  {
    id: "3lag",
    name: "Jack / 3LAG",
    client_tag: "3lag",
    direction: "outbound",
    material: "Dog food processing waste - protein-rich organic byproduct",
    address: "Phoenix metro area",
    pricing_notes: "Negotiated flat rate per pickup. Invoiced monthly.",
    load_frequency: "1-2 loads per week, flexible scheduling",
    contacts: [
      { name: "Jack Mendoza", role: "Owner", phone: "(602) 555-0301", email: "jack@3lag.com" },
      { name: "Maria Gutierrez", role: "Office Manager", phone: "(602) 555-0302", email: "maria@3lag.com" },
      { name: "Tony Reyes", role: "Warehouse Lead", phone: "(602) 555-0303", email: "tony@3lag.com" },
      { name: "Lisa Park", role: "Scheduling", phone: "(602) 555-0304", email: "lisa@3lag.com" },
    ],
  },
];

const TEAM: TeamMember[] = [
  { name: "Simon", role: "Yard Manager", phone: "(602) 555-1001", email: "simon@soilseedandwater.com" },
  { name: "Johnathan", role: "Logistics", phone: "(602) 555-1002", email: "jcarrasco@soilseedandwater.com" },
  { name: "Rodo", role: "Operations", phone: "(602) 555-1003", email: "ralvarez@soilseedandwater.com" },
];

const DRIVERS: Driver[] = [
  { name: "Don LaFollette", phone: "(602) 555-2001" },
  { name: "Coy Cooper", phone: "(602) 555-2002" },
  { name: "Shane Grimm", phone: "(602) 555-2003" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Resources() {
  const { token } = useAuth();

  const [expandedDeal, setExpandedDeal] = useState<string | null>(null);
  const [bolCounts, setBolCounts] = useState<Record<string, number>>({});
  const [codCounts, setCodCounts] = useState<Record<string, number>>({});

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  // -----------------------------------------------------------------------
  // Fetch BOL and COD counts per deal
  // -----------------------------------------------------------------------

  const fetchCounts = useCallback(async () => {
    try {
      const results = await Promise.all(
        DEALS.map(async (deal) => {
          const [bolRes, codRes] = await Promise.all([
            apiFetch(`/api/ops/bols?client_tag=${deal.client_tag}`, { headers }).catch(() => null),
            apiFetch(`/api/ops/cods?client_tag=${deal.client_tag}`, { headers }).catch(() => null),
          ]);

          let bolCount = 0;
          let codCount = 0;

          if (bolRes && bolRes.ok) {
            const bolData = await bolRes.json();
            bolCount = bolData.count ?? (Array.isArray(bolData.bols) ? bolData.bols.length : 0);
          }
          if (codRes && codRes.ok) {
            const codData = await codRes.json();
            codCount = codData.count ?? (Array.isArray(codData.cods) ? codData.cods.length : 0);
          }

          return { tag: deal.client_tag, bolCount, codCount };
        }),
      );

      const bols: Record<string, number> = {};
      const cods: Record<string, number> = {};
      for (const r of results) {
        bols[r.tag] = r.bolCount;
        cods[r.tag] = r.codCount;
      }
      setBolCounts(bols);
      setCodCounts(cods);
    } catch {
      // Counts are supplementary; don't block rendering
    }
  }, [headers]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const toggleDeal = (id: string) => {
    setExpandedDeal((prev) => (prev === id ? null : id));
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="space-y-8 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Resources</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Reference guide for active deals, team contacts, and drivers
        </p>
      </div>

      {/* Active Deals */}
      <section>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Package size={20} className="text-[#264027]" /> Active Deals
        </h2>
        <div className="space-y-3">
          {DEALS.map((deal) => {
            const isExpanded = expandedDeal === deal.id;
            const bolCount = bolCounts[deal.client_tag] ?? 0;
            const codCount = codCounts[deal.client_tag] ?? 0;

            return (
              <div
                key={deal.id}
                className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden"
              >
                {/* Card header */}
                <button
                  onClick={() => toggleDeal(deal.id)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      deal.direction === "inbound"
                        ? "bg-blue-100 dark:bg-blue-900/30"
                        : "bg-green-100 dark:bg-green-900/30"
                    }`}>
                      {deal.direction === "inbound" ? (
                        <ArrowDownToLine size={18} className="text-blue-600 dark:text-blue-400" />
                      ) : (
                        <ArrowUpFromLine size={18} className="text-green-600 dark:text-green-400" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white">{deal.name}</h3>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide ${
                          deal.direction === "inbound"
                            ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                            : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                        }`}>
                          {deal.direction}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">{deal.material}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {/* BOL / COD badges */}
                    <div className="flex gap-2">
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 font-medium">
                        <FileText size={10} /> {bolCount} BOLs
                      </span>
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 font-medium">
                        <DollarSign size={10} /> {codCount} CODs
                      </span>
                    </div>
                    <span className="text-xs text-gray-400 font-medium">{deal.contacts.length} contacts</span>
                    {isExpanded ? (
                      <ChevronDown size={16} className="text-gray-400" />
                    ) : (
                      <ChevronRight size={16} className="text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="border-t border-gray-100 dark:border-gray-800 p-5 space-y-5">
                    {/* Details grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Material</p>
                        <p className="text-sm text-gray-900 dark:text-white">{deal.material}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Address</p>
                        <p className="text-sm text-gray-900 dark:text-white flex items-start gap-1">
                          <MapPin size={13} className="text-gray-400 mt-0.5 shrink-0" />
                          {deal.address}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Pricing</p>
                        <p className="text-sm text-gray-900 dark:text-white">{deal.pricing_notes}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Load Frequency</p>
                        <p className="text-sm text-gray-900 dark:text-white">{deal.load_frequency}</p>
                      </div>
                    </div>

                    {/* Contacts */}
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Contacts</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {deal.contacts.map((c) => (
                          <div
                            key={c.email}
                            className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                          >
                            <div className="w-8 h-8 rounded-full bg-[#264027] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                              {c.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-900 dark:text-white">{c.name}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{c.role}</p>
                              <div className="flex flex-col gap-0.5 mt-1">
                                <a href={`tel:${c.phone}`} className="text-xs text-gray-500 hover:text-[#264027] inline-flex items-center gap-1">
                                  <Phone size={10} /> {c.phone}
                                </a>
                                <a href={`mailto:${c.email}`} className="text-xs text-gray-500 hover:text-[#264027] inline-flex items-center gap-1 truncate">
                                  <Mail size={10} /> {c.email}
                                </a>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* SSW Team */}
      <section>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Users size={20} className="text-[#264027]" /> SSW Team
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {TEAM.map((member) => (
            <div
              key={member.name}
              className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-5"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-[#264027] text-white text-sm font-bold flex items-center justify-center">
                  {member.name.slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">{member.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{member.role}</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <a href={`tel:${member.phone}`} className="text-xs text-gray-500 hover:text-[#264027] flex items-center gap-2">
                  <Phone size={12} /> {member.phone}
                </a>
                <a href={`mailto:${member.email}`} className="text-xs text-gray-500 hover:text-[#264027] flex items-center gap-2 truncate">
                  <Mail size={12} /> {member.email}
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Drivers */}
      <section>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Truck size={20} className="text-[#264027]" /> Drivers
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {DRIVERS.map((driver) => (
            <div
              key={driver.name}
              className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-5"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                  <Truck size={18} className="text-gray-500 dark:text-gray-400" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">{driver.name}</p>
                  <a href={`tel:${driver.phone}`} className="text-xs text-gray-500 hover:text-[#264027] flex items-center gap-1 mt-0.5">
                    <Phone size={10} /> {driver.phone}
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
